import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Train, Upload as UploadIcon, AlertTriangle, TrendingUp, List, Settings as SettingsIcon, FileClock, Download, Smartphone, GitCompareArrows } from "lucide-react";
import { useWeek } from "@/context/WeekContext";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { toast } from "sonner";

const navItems = [
  { to: "/", label: "Dashboard", icon: TrendingUp, testid: "nav-dashboard" },
  { to: "/pair-analysis", label: "Pair View", icon: GitCompareArrows, testid: "nav-pair-analysis" },
  { to: "/upload", label: "Upload", icon: UploadIcon, testid: "nav-upload" },
  { to: "/unmatched", label: "Unmatched", icon: AlertTriangle, testid: "nav-unmatched" },
  { to: "/trends", label: "Trends", icon: TrendingUp, testid: "nav-trends" },
  { to: "/pairs", label: "Pair Master", icon: List, testid: "nav-pairs" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/audit", label: "Audit Log", icon: FileClock, testid: "nav-audit" },
];

export default function Layout() {
  const { week, setWeek, weeks } = useWeek();
  const { canInstall, installed, install } = usePwaInstall();
  const navigate = useNavigate();

  const handleInstall = async () => {
    const outcome = await install();
    if (outcome === "accepted") {
      toast.success("App installed");
    } else if (outcome === "unavailable") {
      toast.info("Open in Chrome on Android → menu (⋮) → 'Add to Home screen'");
    }
  };

  const handleExport = () => {
    if (!week) { toast.error("No week selected"); return; }
    window.open(`${API}/export?week_label=${encodeURIComponent(week)}`, "_blank");
    toast.success("Excel export started");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col text-slate-900">
      {/* Top nav */}
      <header className="bg-slate-900 text-white sticky top-0 z-40 border-b border-slate-800 shadow-md">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            data-testid="brand-link"
            className="flex items-center gap-2.5 group"
          >
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <Train className="h-5 w-5 text-white" />
            </div>
            <div className="text-left leading-tight">
              <div className="font-display font-bold text-base tracking-tight">RailMADAD</div>
              <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">Pair Analyzer v2</div>
            </div>
          </button>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-[11px] uppercase font-semibold text-slate-400 tracking-wider">Week</span>
              <Select value={week || ""} onValueChange={setWeek}>
                <SelectTrigger data-testid="week-selector-dropdown" className="h-9 min-w-[130px] bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {weeks.length === 0 && <div className="p-2 text-xs text-slate-500">No weeks</div>}
                  {weeks.map((w) => (
                    <SelectItem key={w} value={w} data-testid={`week-option-${w}`}>{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!installed && (
              <Button
                data-testid="install-app-btn"
                onClick={handleInstall}
                variant="outline"
                className="h-9 border-slate-600 bg-transparent text-white hover:bg-slate-800 hover:text-white"
              >
                <Smartphone className="h-4 w-4 mr-1.5" /> Install App
              </Button>
            )}
            <Button
              data-testid="export-excel-btn"
              onClick={handleExport}
              className="bg-blue-600 hover:bg-blue-500 text-white h-9"
            >
              <Download className="h-4 w-4 mr-1.5" /> Export
            </Button>
          </div>
        </div>

        {/* Sub-nav tabs */}
        <nav className="bg-slate-900 border-t border-slate-800">
          <div className="max-w-[1600px] mx-auto px-2 sm:px-6 flex items-center gap-1 overflow-x-auto scrollbar-thin">
            {navItems.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? "text-white border-blue-500"
                      : "text-slate-400 border-transparent hover:text-white hover:border-slate-600"
                  }`
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>

      <footer className="mt-auto border-t border-slate-200 bg-white py-3 px-6 text-center text-[11px] text-slate-500 font-mono">
        RailMADAD Ops Analyzer · v2 · 4 Complaint Heads · 8-Week Trends
      </footer>
    </div>
  );
}
